import { Box, BoxProps, Paper, SvgIcon, Typography } from '@mui/material';
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import { ToggleButton } from '../primitives/ToggleButton/ToggleButton';
import { ToggleButtonGroup } from '../primitives/ToggleButton/ToggleButtonGroup';

export enum EDisplayMode {
  Assets = 'assets',
  Positions = 'positions',
}

interface ListWrapperProps {
  titleComponent?: ReactNode;
  localStorageName?: string;
  subTitleComponent?: ReactNode;
  subChildrenComponent?: ReactNode;
  topInfo?: ReactNode;
  children: ReactNode;
  filterComponent?: ReactNode;
  withTopMargin?: boolean;
  noData?: boolean;
  wrapperSx?: BoxProps['sx'];
  tooltipOpen?: boolean;
  displayMode?: EDisplayMode;
  onSwitchDisplayMode?: Dispatch<SetStateAction<EDisplayMode>>;
  hasCollapseButton?: boolean;
}

export const ListWrapper = ({
  children,
  displayMode,
  onSwitchDisplayMode,
  titleComponent,
  filterComponent,
  subTitleComponent,
  subChildrenComponent,
  topInfo,
  withTopMargin,
  noData,
  wrapperSx,
  tooltipOpen,
  hasCollapseButton,
}: ListWrapperProps) => {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setCollapsed(false);
  }, [hasCollapseButton]);

  return (
    <Paper
      sx={(theme) => ({
        mt: withTopMargin ? 4 : 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        overflow: 'hidden',
        pb: collapsed ? 2 : 0,
      })}
    >
      <Box
        sx={{
          px: { xs: 4, xsm: 6 },
          pt: { xs: 3.5, xsm: 4 },
          pb: 1,
          ...wrapperSx,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
            flexWrap: { xs: 'wrap', md: 'unset' },
          }}
        >
          <Box sx={{ width: { xs: 'unset', md: '100%' }}}>
            {titleComponent}
          </Box>
          {hasCollapseButton && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              onClick={() => setCollapsed((prevState) => !prevState)}
            >
              <Typography variant="secondary14" sx={{ color: 'text.secondary' }}>Hide</Typography>
              <SvgIcon sx={{ color: 'text.secondary' }}>
                {collapsed ? <AddRoundedIcon /> : <RemoveRoundedIcon />}
              </SvgIcon>
            </Box>
          )}
          {filterComponent && (
            <Box sx={{
              width: { xs: '100%', md: 'unset' },
              mb: { xs: 1, md: 3 },
            }}>
              {filterComponent}
            </Box>
          )}
          {!!displayMode && !noData && !collapsed && (
            <ToggleButtonGroup
              color="primary"
              exclusive={true}
              value={displayMode}
              onChange={(_event, value) => {
                value && onSwitchDisplayMode?.(value);
                const regex = /<Trans>(.*?)<\/Trans>/s;
                const match = titleComponent?.toString().match(regex);
                const titleText = match?.[1] || 'unknown';
                window.gtag('event', 'lending_choose_asset_click', {
                  value: value,
                  place: titleText,
                });
              }}
              sx={{
                flex: { xs: '1 1 auto', md: 'unset' },
                width: { xs: '100%', md: 'unset' },
                mt: { xs: 4, md: 'unset' },
                mb: { xs: 1, md: 'unset' },
              }}
            >
              <ToggleButton
                size="small"
                value={EDisplayMode.Assets}
                sx={{ width: { xs: '50%', md: 'unset' } }}
              >
                Assets
              </ToggleButton>
              <ToggleButton
                size="small"
                value={EDisplayMode.Positions}
                sx={{ width: { xs: '50%', md: 'unset' } }}
              >
                Positions
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {subTitleComponent && (
          <Box sx={{
            mt: { xs: 2, md: 1 },
            mb: 3,
          }}>
            {subTitleComponent}
          </Box>
        )}
      </Box>

      {topInfo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: { xs: 4, xsm: 6 },
            pb: { xs: collapsed && !noData ? 6 : 2, xsm: collapsed && !noData ? 6 : 2 },
            overflowX: tooltipOpen ? 'hidden' : 'auto',
          }}
        >
          {topInfo}
        </Box>
      )}
      {subChildrenComponent && !collapsed && (
        <Box sx={{ marginBottom: { xs: 2, xsm: 0 } }}>
          {subChildrenComponent}
        </Box>
      )}
      <Box sx={{ display: collapsed ? 'none' : 'block' }}>{children}</Box>
    </Paper>
  );
};
